import {useEffect,useRef,useState} from 'react';
import {AnimatePresence,motion} from 'framer-motion';
import '../entrance.css';
import '../entrance-interaction.css';

const doorTransition={duration:1.9,ease:[0.76,0,0.24,1]};

function DoorGrid(){
  return(
    <div className="entrance-door__grid" aria-hidden="true">
      {Array.from({length:24},(_,index)=><i key={index}/>)}
    </div>
  );
}

function DoorControl({type,side,english,opening,onOpen}){
  const isCrest=type==='crest';
  const label=english
    ? `Open the doors using the ${isCrest?'crest':'handle'}`
    : `按${isCrest?'旬字門章':'門把'}開門`;

  return(
    <motion.button
      className={`entrance-door__${type} entrance-door__control`}
      type="button"
      onClick={onOpen}
      disabled={opening}
      aria-label={label}
      whileHover={opening?undefined:{scale:1.06}}
      whileTap={opening?undefined:{scale:0.95}}
      transition={{duration:0.22}}
    >
      {isCrest?(
        <span aria-hidden="true">{side==='left'?'旬':'心'}</span>
      ):(
        <i aria-hidden="true"/>
      )}
    </motion.button>
  );
}

function ShojiDoor({side,opening,english,onOpen}){
  const destination=side==='left'?'-101%':'101%';

  return(
    <motion.div
      className={`entrance-door entrance-door--${side}`}
      animate={{x:opening?destination:0}}
      transition={doorTransition}
    >
      <div className="entrance-door__wood"/>
      <DoorGrid/>
      <div className="entrance-door__paper" aria-hidden="true"/>
      <DoorControl
        type="crest"
        side={side}
        english={english}
        opening={opening}
        onOpen={onOpen}
      />
      <DoorControl
        type="handle"
        side={side}
        english={english}
        opening={opening}
        onOpen={onOpen}
      />
    </motion.div>
  );
}

function EntranceArchitecture({opening}){
  return(
    <motion.div
      className="entrance-architecture"
      animate={{opacity:opening?0.55:1,scale:opening?1.025:1}}
      transition={{duration:1.2,ease:'easeOut'}}
      aria-hidden="true"
    >
      <div className="entrance-roof">
        <div className="entrance-roof__tiles">
          {Array.from({length:18},(_,index)=><i key={index}/>)}
        </div>
      </div>
      <div className="entrance-pillar entrance-pillar--left"/>
      <div className="entrance-pillar entrance-pillar--right"/>
      <div className="entrance-beam"/>
      <div className="entrance-stone entrance-stone--left"/>
      <div className="entrance-stone entrance-stone--right"/>
    </motion.div>
  );
}

function RyoteiIdentity({copy,english,opening}){
  return(
    <motion.div
      className="entrance-identity"
      animate={{opacity:opening?0:1,y:opening?-18:0}}
      transition={{duration:0.65}}
    >
      <div className="entrance-plaque">
        <small>
          {english
            ?'A CENTURY OF CRAFT · ONE SEATING A DAY'
            :'創業百年 · 一日一席'}
        </small>
        <strong>{english?'SHUNSHIN RYOTEI':'旬心料亭'}</strong>
        <span>OMAKASE.HK</span>
      </div>

      <div className="entrance-rule">
        <span>ACADEMY RULE · 01</span>
        <p>{copy.rule}</p>
      </div>

      <div className="entrance-lantern entrance-lantern--left" aria-hidden="true">
        <i/>
        <b>旬</b>
      </div>
      <div className="entrance-lantern entrance-lantern--right" aria-hidden="true">
        <i/>
        <b>心</b>
      </div>
    </motion.div>
  );
}

function AcademyInterior({english,opening}){
  return(
    <motion.div
      className="entrance-interior"
      animate={{
        scale:opening?1.22:1,
        filter:opening?'brightness(1.18)':'brightness(0.06)'
      }}
      transition={{
        duration:opening?2.6:0.3,
        ease:[0.22,0.7,0.2,1]
      }}
      aria-hidden="true"
    >
      <div className="entrance-interior__corridor">
        {Array.from({length:7},(_,index)=>(
          <i key={index} style={{'--depth':index}}/>
        ))}
      </div>
      <div className="entrance-interior__floor"/>

      <motion.div
        className="entrance-interior__identity"
        animate={{
          opacity:opening?1:0,
          y:opening?-10:18,
          scale:opening?1:0.94
        }}
        transition={{duration:1.1,delay:opening?0.65:0}}
      >
        <span>BEYOND THE LAST COURSE</span>
        <div className="entrance-interior__crest">
          <small>板</small>
          <b>前</b>
        </div>
        <strong>{english?'SECRET ITAMAE ACADEMY':'裏・板前學院'}</strong>
        <p>
          {english
            ?'The ryotei is the face. The academy lies beyond.'
            :'料亭是表，學院是裏。'}
        </p>
      </motion.div>
    </motion.div>
  );
}

function EntranceLanguage({english,opening,onChange}){
  return(
    <motion.div
      className="entrance-language"
      animate={{opacity:opening?0:1,y:opening?-8:0}}
      transition={{duration:0.35}}
      aria-label={english?'Language selection':'語言選擇'}
    >
      <span>LANGUAGE</span>
      <div>
        <button
          type="button"
          className={!english?'is-active':''}
          onClick={()=>onChange('zh-Hant')}
          disabled={opening}
          aria-pressed={!english}
        >
          中
        </button>
        <i aria-hidden="true"/>
        <button
          type="button"
          className={english?'is-active':''}
          onClick={()=>onChange('en')}
          disabled={opening}
          aria-pressed={english}
        >
          EN
        </button>
      </div>
    </motion.div>
  );
}

function EnterButton({english,opening,onOpen}){
  return(
    <motion.div
      className="entrance-invitation"
      animate={{opacity:opening?0:1,y:opening?16:0}}
      transition={{duration:0.4}}
    >
      <span className="entrance-invitation__line" aria-hidden="true"/>
      <button
        className="entrance-action"
        type="button"
        onClick={onOpen}
        disabled={opening}
        aria-label={english?'Open the doors and enter':'打開門並進入'}
      >
        <span className="entrance-action__copy">
          <b>{english?'OPEN THE DOORS':'開門入席'}</b>
          <small>
            {english?'TOUCH THE CREST OR HANDLE':'亦可按門章或門把'}
          </small>
        </span>
        <i aria-hidden="true">入</i>
      </button>
      <span className="entrance-invitation__line" aria-hidden="true"/>
    </motion.div>
  );
}

export default function EntranceLoader({visible,copy,lang,onComplete}){
  const [opening,setOpening]=useState(false);
  const completeTimer=useRef();
  const english=lang==='en';

  useEffect(()=>{
    if(!visible)setOpening(false);
  },[visible]);

  useEffect(()=>{
    if(!visible)return undefined;
    const previousOverflow=document.body.style.overflow;
    document.body.style.overflow='hidden';

    return()=>{
      document.body.style.overflow=previousOverflow;
      window.clearTimeout(completeTimer.current);
    };
  },[visible]);

  const openDoors=()=>{
    if(opening)return;
    setOpening(true);
    completeTimer.current=window.setTimeout(onComplete,2750);
  };

  const changeLanguage=(nextLang)=>{
    if(opening||nextLang===lang)return;
    const nextPath=nextLang==='en'?'/en/':'/';
    window.history.pushState({},'',nextPath);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return(
    <AnimatePresence>
      {visible&&(
        <motion.div
          className={`entrance-loader ${opening?'is-opening':''}`}
          initial={{opacity:1}}
          exit={{opacity:0}}
          transition={{duration:0.8,ease:'easeInOut'}}
        >
          <AcademyInterior english={english} opening={opening}/>
          <ShojiDoor
            side="left"
            opening={opening}
            english={english}
            onOpen={openDoors}
          />
          <ShojiDoor
            side="right"
            opening={opening}
            english={english}
            onOpen={openDoors}
          />
          <EntranceArchitecture opening={opening}/>
          <RyoteiIdentity copy={copy} english={english} opening={opening}/>
          <EntranceLanguage
            english={english}
            opening={opening}
            onChange={changeLanguage}
          />
          <EnterButton english={english} opening={opening} onOpen={openDoors}/>

          <motion.div
            className="entrance-threshold"
            animate={{opacity:opening?0:1,y:opening?50:0}}
            transition={{duration:0.7}}
            aria-hidden="true"
          />

          <motion.div
            className="entrance-light"
            initial={false}
            animate={{
              opacity:opening?[0,0,0.22,0.78]:0,
              scale:opening?[0.35,0.35,1.05,1.85]:0.35
            }}
            transition={{
              duration:2.45,
              times:[0,0.16,0.48,1],
              ease:'easeOut'
            }}
            aria-hidden="true"
          />

          <motion.div
            className="entrance-camera"
            initial={false}
            animate={{
              opacity:opening?[0,0,0.72]:0,
              scale:opening?1.18:1
            }}
            transition={{duration:2.7,times:[0,0.68,1]}}
            aria-hidden="true"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}